import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { FaArrowLeft } from 'react-icons/fa';
import { AudioContext } from '../context/AudioContext';
import TrackItem from './TrackItem';
import '../styles/TrackDetail.css';

const TrackDetail = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const { handleTrackClick } = useContext(AudioContext);
  const currentUser = getAuth().currentUser;
  const firebaseUid = currentUser?.uid;

  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinnedTracks, setPinnedTracks] = useState([]);

  useEffect(() => {
    const fetchTrackAndPinned = async () => {
      try {
        // Fetch the single track by ID
        const trackResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/${trackId}`
        );
        setTrack(trackResponse.data);

        // Fetch pinned tracks to see if this track is pinned
        const pinnedResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/pinned`,
          { params: { firebaseUid } }
        );
        setPinnedTracks(pinnedResponse.data);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching track:', error);
        setLoading(false);
      }
    };

    fetchTrackAndPinned();
  }, [trackId, firebaseUid]);

  const handlePinTrack = async (trackId) => {
    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/pin', {
        firebaseUid,
        trackId,
      });
      // Re-fetch pinned tracks
      const pinnedResponse = await axios.get(
        `https://sync-api-troz.onrender.com/api/uploads/pinned`,
        { params: { firebaseUid } }
      );
      setPinnedTracks(pinnedResponse.data);
    } catch (error) {
      console.error('Error pinning track:', error);
    }
  };

  const handleUnpinTrack = async (trackId) => {
    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/unpin', {
        firebaseUid,
        trackId,
      });
      // Update pinned tracks state
      setPinnedTracks((prev) => prev.filter((t) => t._id !== trackId));
    } catch (error) {
      console.error('Error unpinning track:', error);
    }
  };

  const handleDeleteTrack = async (trackId) => {
    const confirmed = window.confirm('Are you sure you want to delete this track?');
    if (confirmed) {
      try {
        await axios.delete('https://sync-api-troz.onrender.com/api/uploads/delete-track', {
          data: { trackId, firebaseUid },
        });
        window.alert('Track deleted successfully');
        navigate(-1);
      } catch (error) {
        console.error('Error deleting track:', error);
        window.alert('Failed to delete track');
      }
    }
  };

  const handleLikeTrack = async (trackId, trackUserId) => {
    if (!currentUser) {
      window.alert('You must be logged in to like a track.');
      return;
    }

    if (trackUserId === currentUser.uid) {
      window.alert("You can't like your own track.");
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        'https://sync-api-troz.onrender.com/api/uploads/like',
        {
          trackId,
          firebaseUid: currentUser.uid,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      window.alert('Track liked successfully!');
    } catch (error) {
      console.error('Error liking track:', error.response?.data || error.message);
      if (error.response?.data?.message === 'You have already liked this track') {
        window.alert('You have already liked this track.');
      } else {
        window.alert('An error occurred while liking the track.');
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="loading-text">Loading track...</p>
      </div>
    );
  }

  if (!track) {
    return (
      <div className="loading-container">
        <p className="loading-text">Track not found.</p>
      </div>
    );
  }

  const isPinned = pinnedTracks.some((t) => t._id === track._id);

  return (
    <div className="track-detail-container">
      <div className="profile-banner">
        {track.coverPhoto ? (
          <img src={track.coverPhoto} alt={track.title} className="banner-image" />
        ) : (
          <div className="banner-placeholder"></div>
        )}
        <p className="track-title-on-banner">{track.title}</p>
      </div>

      <div className="scroll-content">
        <div className="header">
          <button onClick={() => navigate(-1)} className="back-button">
            <FaArrowLeft size={24} />
          </button>
          <p className="header-text">{track.artistName}</p>
          <div className="placeholder"></div>
        </div>

        <TrackItem
          key={track._id}
          track={track}
          onPlay={() => handleTrackClick(track)}
          onPin={() => handlePinTrack(track._id)}
          onUnpin={() => handleUnpinTrack(track._id)}
          isPinned={isPinned}
          onDelete={() => handleDeleteTrack(track._id)}
          onLike={() => handleLikeTrack(track._id, track.user?.firebaseUid)}
          currentUserId={firebaseUid}
          isOwner={false}
          showPinButton={false}
          showDeleteButton={false}
        />
      </div>
    </div>
  );
};

export default TrackDetail;